import { Suspense, lazy } from 'react';
import { useSettingsContext } from '../../context/settings';
import useCarSearch from '../../hooks/use-car-search';
import useLocales from '../../hooks/useLocales';
import getFilterElement from '../../modules/fahrzeugsuche/filter/utils/getFilterElement';
import getStyleQuery from '../../utils/tools/getStyleQuery';
import uniqueArray from '../../utils/tools/uniqueArray';
import CarListView from '../car-list-view';
import FormProvider from '../hook-form/form-provider/component';
import Iconify from '../iconify/component';
import type { CarSelectDialogProps } from './types';

const Button = lazy(() => import('@mui/material/Button' /* webpackChunkName: "Mui_Button" */));
const Dialog = lazy(() => import('@mui/material/Dialog' /* webpackChunkName: "Mui_Dialog" */));
const DialogActions = lazy(() => import('@mui/material/DialogActions' /* webpackChunkName: "Mui_DialogActions" */));
const DialogContent = lazy(() => import('@mui/material/DialogContent' /* webpackChunkName: "Mui_DialogContent" */));
const DialogTitle = lazy(() => import('@mui/material/DialogTitle' /* webpackChunkName: "Mui_DialogTitle" */));
const Grid = lazy(() => import('@mui/material/Grid2' /* webpackChunkName: "Mui_Grid" */));

export default function CarSelectDialog(props: CarSelectDialogProps) {
    const { allFaarenVehicles, defaultValues, methods, module, openCarSelector, values } = props;

    const { translate } = useLocales();
    const settings = useSettingsContext();
    const {
        formKey,
        handleSubmit: carSearchHandleSubmit,
        loading: carSearchLoading,
        methods: carSearchMethods,
        results: carSearchResults,
        setValue: carSearchSetValue,
        values: carSearchValues,
    } = useCarSearch({
        allFaarenVehicles,
        disableAutoLoad: openCarSelector?.value !== true,
        isContact: true,
        module,
        navigate: undefined,
        params: undefined,
        query: undefined,
    });

    const {
        formState: { isSubmitting },
        reset,
        setValue,
    } = methods;

    return (
        <Suspense fallback={<div />}>
            <Dialog data-testid="CarSelectDialog" open onClose={openCarSelector.onFalse} fullWidth maxWidth="md">
                <DialogTitle>{translate('common:buttons.chooseVehicleHeading')}</DialogTitle>
                <DialogContent>
                    <FormProvider formKey={`${formKey}_UseContact_CarSelect`} methods={carSearchMethods} onSubmit={carSearchHandleSubmit}>
                        <Grid container spacing={2} sx={{ [getStyleQuery()]: { mt: 1 } }}>
                            {uniqueArray(
                                settings?.contact?.carSearchFilters?.map((key) => (
                                    <Grid key={`useContact_carSearchFilter_${key}`} size={{ xs: 12, sm: 4, lg: 3 }}>
                                        {getFilterElement(key, carSearchResults, settings, translate, true, carSearchLoading)}
                                    </Grid>
                                ))
                            )}
                            <Grid size={{ xs: 12 }}>
                                <CarListView
                                    cars={carSearchResults?.cars}
                                    filter={carSearchValues}
                                    list
                                    loading={carSearchLoading}
                                    module={module}
                                    mini
                                    numFound={carSearchResults?.numFound}
                                    onFilters={carSearchSetValue}
                                    onMiniSelectCar={(_e, newCar) => {
                                        setValue('car', newCar);
                                        openCarSelector.onFalse();
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </FormProvider>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'flex-start' }}>
                    <Button
                        id="detailview2_dialog-without-car_button_close"
                        color="primary"
                        disabled={isSubmitting}
                        variant="soft"
                        startIcon={<Iconify icon="mdi:close" />}
                        onClick={() => {
                            // Preserve values for accessoires & accessoiresPrice when Dialog is closed
                            reset({
                                ...defaultValues,
                                accessoires: values?.accessoires,
                                accessoiresPrice: values?.accessoiresPrice,
                            });
                            openCarSelector.onFalse();
                        }}
                        sx={{
                            [getStyleQuery()]: {
                                'color': (theme) => theme.palette.primary.main,
                                '&:hover': { borderColor: (theme) => theme.palette.primary.main, boxShadow: '0 0 0 0.5px (theme) => theme.palette.primary.main' },
                            },
                        }}
                    >
                        {translate('common:buttons.close')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Suspense>
    );
}
